/**
 * PDF Export Utility for Frontend
 * Uses jsPDF and jspdf-autotable for client-side PDF generation
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// For jspdf-autotable v5.x, we need to use autoTable as a function
// It extends jsPDF prototype when imported, but we can also use it directly

/**
 * Export simulation results to PDF with unified bridge structure
 * @param {Object} simulationResults - Simulation results object
 * @param {Object} hierarchy - Hierarchical structure from transformToHierarchy
 * @param {Object} bridge - Bridge object with level config
 * @param {boolean} showDecayPercent - Whether to include decay percentages
 * @param {string} filename - Output filename
 */
export const exportSimulationToPDF = (simulationResults, hierarchy = null, bridge = null, showDecayPercent = true, filename = null) => {
  const defaultFilename = `simulation_results_${Date.now()}.pdf`;
  const outputFilename = filename || defaultFilename;
  
  const doc = new jsPDF('landscape'); // Use landscape for wider tables
  
  // Title
  doc.setFontSize(20);
  const title = bridge ? `${bridge.name || 'Bridge'} - Simulation Results` : 'Simulation Results Report';
  doc.text(title, doc.internal.pageSize.width / 2, 20, { align: 'center' });
  
  let yPosition = 35;
  
  // Summary Section
  doc.setFontSize(16);
  doc.text('Summary', 14, yPosition);
  yPosition += 10;
  
  doc.setFontSize(12);
  const summaryData = [];
  
  if (bridge) {
    summaryData.push(['Bridge Name', bridge.name || 'N/A']);
    summaryData.push(['Bridge Code', bridge.bridgeCode || 'N/A']);
  }
  summaryData.push(['Simulation Date', new Date().toLocaleDateString()]);
  
  // Helper function to extract numeric value (handles both numbers and objects)
  const getNumericValue = (value, defaultValue = 0) => {
    if (typeof value === 'number') {
      return value;
    }
    if (value && typeof value === 'object') {
      // For Monte Carlo statistics objects, use mean or median
      return value.mean || value.median || defaultValue;
    }
    return defaultValue;
  };
  
  // Check if this is a Monte Carlo simulation
  const isMonteCarlo = simulationResults.mode === 'monte_carlo';
  
  if (simulationResults.summary) {
    // For Monte Carlo, summary.totalCost and summary.npv are objects with statistics
    // Extract the numeric value (mean or median)
    const totalCostValue = isMonteCarlo 
      ? getNumericValue(simulationResults.summary.totalCost)
      : (simulationResults.summary.totalCost || 0);
    const npvValue = isMonteCarlo
      ? getNumericValue(simulationResults.summary.npv)
      : (simulationResults.summary.npv || 0);
    
    summaryData.push(['Total Cost', `$${totalCostValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`]);
    summaryData.push(['NPV', `$${npvValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`]);
    
    // Average Yearly Cost - check if it's an object or number
    const avgYearlyCostValue = getNumericValue(simulationResults.summary.avgYearlyCost);
    summaryData.push(['Average Yearly Cost', `$${avgYearlyCostValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`]);
    summaryData.push(['Time Horizon (years)', String(simulationResults.timeHorizonYears || 30)]);
  }
  
  if (simulationResults.scenario) {
    summaryData.push(['Decay Threshold', (simulationResults.scenario.decayThreshold || 2.0) + '%']);
    summaryData.push(['Max Consecutive Touch-ups', String(simulationResults.scenario.maxConsecutiveTouchUps || 2)]);
  }
  
  // For Monte Carlo, show additional statistics
  if (isMonteCarlo && simulationResults.statistics) {
    const stats = simulationResults.statistics;
    if (stats.totalCost && typeof stats.totalCost === 'object') {
      summaryData.push(['P50 (Median)', `$${(stats.totalCost.median || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`]);
      summaryData.push(['Mean', `$${(stats.totalCost.mean || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`]);
    } else {
      // Fallback for old format
      summaryData.push(['P50 (Median)', `$${(stats.p50 || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`]);
      summaryData.push(['Mean', `$${(stats.mean || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`]);
    }
  } else if (simulationResults.statistics && !isMonteCarlo) {
    // For non-Monte Carlo simulations
    summaryData.push(['P50 (Median)', `$${(simulationResults.statistics.p50 || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`]);
    summaryData.push(['Mean', `$${(simulationResults.statistics.mean || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`]);
  }
  
  autoTable(doc, {
    startY: yPosition,
    head: [['Metric', 'Value']],
    body: summaryData,
    theme: 'striped',
    headStyles: { fillColor: [200, 200, 200] },
    columnStyles: { 0: { cellWidth: 60 }, 1: { cellWidth: 80 } }
  });
  
  yPosition = doc.lastAutoTable.finalY + 15;
  
  // Hierarchical Results Table (if available)
  if (hierarchy) {
    if (yPosition > 200) {
      doc.addPage('landscape');
      yPosition = 20;
    }
    
    doc.setFontSize(16);
    doc.text('Hierarchical Results', 14, yPosition);
    yPosition += 10;
    
    const timeHorizon = simulationResults.timeHorizonYears || 30;
    const startYear = simulationResults.simulationStartYear != null && simulationResults.simulationStartYear > 0
      ? simulationResults.simulationStartYear
      : new Date().getFullYear();
    const allYears = [];
    for (let i = 1; i <= timeHorizon; i++) {
      allYears.push(startYear + i - 1);
    }
    
    // Build hierarchical table data
    const buildHierarchicalRows = (node, level = 0, rows = []) => {
      const indent = '  '.repeat(level);
      const row = [
        indent + (node.name || 'Unnamed'),
        node.code || '',
        node.area ? parseFloat(node.area).toFixed(2) : '0.00'
      ];
      
      // Add costs for first 5 years (to fit on page)
      const yearsToShow = allYears.slice(0, 5);
      let rowTotal = 0;
      yearsToShow.forEach(year => {
        const yearData = node.years?.find(y => y.year === year);
        const cost = yearData?.cost || 0;
        row.push(`$${cost.toFixed(2)}`);
        rowTotal += cost;
      });
      row.push(`$${rowTotal.toFixed(2)}`);
      
      rows.push(row);
      
      // Process children (limit depth for PDF readability)
      if (node.children && node.children.length > 0 && level < 4) {
        node.children.forEach(child => {
          buildHierarchicalRows(child, level + 1, rows);
        });
      }
      
      return rows;
    };
    
    const hierarchicalData = buildHierarchicalRows(hierarchy);
    
    // Build header
    const headerRow = ['Item Name', 'Code', 'Area (m²)'];
    const yearsToShow = allYears.slice(0, 5);
    yearsToShow.forEach(year => {
      headerRow.push(year.toString());
    });
    headerRow.push('Total (5 Years)');
    
    autoTable(doc, {
      startY: yPosition,
      head: [headerRow],
      body: hierarchicalData,
      theme: 'striped',
      headStyles: { fillColor: [200, 200, 200], fontSize: 8 },
      bodyStyles: { fontSize: 7 },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 30 },
        2: { cellWidth: 20 },
        ...Object.fromEntries(yearsToShow.map((_, i) => [3 + i, { cellWidth: 25 }])),
        [3 + yearsToShow.length]: { cellWidth: 25 }
      },
      margin: { left: 14, right: 14 }
    });
    
    yPosition = doc.lastAutoTable.finalY + 15;
    
    // Note about additional years
    if (allYears.length > 5) {
      doc.setFontSize(10);
      doc.text(`Note: Showing first 5 years. Full data available in Excel export.`, 14, yPosition);
      yPosition += 10;
    }
  }
  
  // Elements List
  if (simulationResults.spans && simulationResults.spans.length > 0) {
    if (yPosition > 200) {
      doc.addPage('landscape');
      yPosition = 20;
    }
    
    doc.setFontSize(16);
    doc.text('Elements Included', 14, yPosition);
    yPosition += 10;
    
    const elementsData = simulationResults.spans.map(span => [
      span.spanCode || span.span_code || span.element_code || 'N/A',
      span.spanName || span.span_name || span.element_name || 'N/A',
      `${(span.totalArea || span.total_area || 0).toLocaleString('en-US')} m²`,
      span.corrosionCategory || span.corrosion_category || 'N/A',
      span.damageLevel || span.damage_level || 'N/A'
    ]);
    
    autoTable(doc, {
      startY: yPosition,
      head: [['Element Code', 'Element Name', 'Area (m²)', 'Corrosion Category', 'Damage Level']],
      body: elementsData,
      theme: 'striped',
      headStyles: { fillColor: [200, 200, 200], fontSize: 9 },
      bodyStyles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 50 },
        2: { cellWidth: 30 },
        3: { cellWidth: 40 },
        4: { cellWidth: 30 }
      }
    });
  }
  
  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(
      `Generated on ${new Date().toLocaleString()} - Page ${i} of ${pageCount}`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
  }
  
  // Save
  doc.save(outputFilename);
};

/**
 * Export repair project to PDF
 * @param {Object} projectData - Repair project data
 * @param {string} filename - Output filename
 */
export const exportRepairProjectToPDF = (projectData, filename = null) => {
  const defaultFilename = `repair_project_${projectData.id || Date.now()}.pdf`;
  const outputFilename = filename || defaultFilename;
  
  const doc = new jsPDF();
  
  // Get level labels from bridge configuration
  const levelLabels = projectData.levelLabels || {};
  let areaDataLevelLabel = 'Elements';
  let childLevelLabel = 'Child Elements';
  
  // Find the level that holds area data
  for (const [levelKey, levelConfig] of Object.entries(levelLabels)) {
    if (levelConfig?.holdsAreaData) {
      areaDataLevelLabel = levelConfig.label || 'Elements';
    }
    if (levelConfig?.holdsChildData) {
      childLevelLabel = levelConfig.label || 'Child Elements';
    }
  }
  
  // Title
  doc.setFontSize(20);
  doc.text('Repair Project Report', 105, 20, { align: 'center' });
  
  let yPosition = 35;
  
  // Project Information
  doc.setFontSize(16);
  doc.text('Project Information', 14, yPosition);
  yPosition += 10;
  
  const projectInfo = [
    ['Project Name', projectData.project_name || 'N/A'],
    ['Project Code', projectData.project_code || 'N/A'],
    ['Bridge', projectData.bridge_name || 'N/A'],
    ['Status', projectData.status || 'N/A'],
    ['Planned Year', projectData.planned_year || 'N/A'],
    ['Planned Start', projectData.planned_start_date || 'N/A'],
    ['Planned End', projectData.planned_end_date || 'N/A']
  ];
  
  if (projectData.actual_start_date) {
    projectInfo.push(['Actual Start', projectData.actual_start_date]);
  }
  if (projectData.actual_end_date) {
    projectInfo.push(['Actual End', projectData.actual_end_date]);
  }
  
  autoTable(doc, {
    startY: yPosition,
    head: [['Field', 'Value']],
    body: projectInfo,
    theme: 'striped',
    headStyles: { fillColor: [200, 200, 200] }
  });
  
  yPosition = doc.lastAutoTable.finalY + 15;
  
  // Cost Summary
  if (yPosition > 250) {
    doc.addPage();
    yPosition = 20;
  }
  
  doc.setFontSize(16);
  doc.text('Cost Summary', 14, yPosition);
  yPosition += 10;
  
  const estimatedTotal = projectData.estimated_cost || 0;
  const actualTotal = projectData.actual_cost || 0;
  const variance = actualTotal - estimatedTotal;
  
  const costData = [
    ['Estimated Cost', `$${estimatedTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
    ['Actual Cost', `$${actualTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
    ['Variance', `$${variance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`]
  ];
  
  autoTable(doc, {
    startY: yPosition,
    head: [['Category', 'Amount']],
    body: costData,
    theme: 'striped',
    headStyles: { fillColor: [200, 200, 200] }
  });
  
  yPosition = doc.lastAutoTable.finalY + 15;
  
  // Elements Table (area data level)
  const elements = projectData.elements || projectData.spans || [];
  if (elements.length > 0) {
    if (yPosition > 250) {
      doc.addPage();
      yPosition = 20;
    }
    
    doc.setFontSize(16);
    doc.text(areaDataLevelLabel, 14, yPosition);
    yPosition += 10;
    
    const elementsData = elements.map(element => [
      element.element_code || element.span_code || element.spanCode || 'N/A',
      element.element_name || element.span_name || element.spanName || 'N/A',
      `${(element.total_area || element.totalArea || element.span_total_area || 0).toLocaleString('en-US')} m²`,
      `$${(element.allocated_mobilization_cost || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      `$${(element.estimated_material_cost || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      `$${(element.estimated_labor_cost || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      element.corrosion_category || 'N/A',
      element.damage_severity || 'N/A',
      element.element_status || element.span_status || element.status || 'pending'
    ]);
    
    autoTable(doc, {
      startY: yPosition,
      head: [[`${areaDataLevelLabel} Code`, `${areaDataLevelLabel} Name`, 'Area (m²)', 'Mobilization', 'Material Cost', 'Labor Cost', 'Corrosion', 'Damage', 'Status']],
      body: elementsData,
      theme: 'striped',
      headStyles: { fillColor: [200, 200, 200] },
      styles: { fontSize: 8 }
    });
    
    yPosition = doc.lastAutoTable.finalY + 15;
  }
  
  // Child Elements Table
  const allChildElements = [];
  elements.forEach(element => {
    const childElements = element.childElements || [];
    childElements.forEach(child => {
      allChildElements.push({
        parentCode: element.element_code || element.span_code || 'N/A',
        parentName: element.element_name || element.span_name || 'N/A',
        ...child
      });
    });
  });
  
  if (allChildElements.length > 0) {
    if (yPosition > 250) {
      doc.addPage();
      yPosition = 20;
    }
    
    doc.setFontSize(16);
    doc.text(childLevelLabel, 14, yPosition);
    yPosition += 10;
    
    const childElementsData = allChildElements.map(child => [
      child.parentCode || 'N/A',
      child.child_element_identifier || child.childElementIdentifier || 'N/A',
      `${(child.average_area || child.averageArea || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m²`,
      `${(child.estimated_damage_area || child.estimatedDamageArea || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m²`,
      `${(child.current_damage_area || child.currentDamageArea || child.actual_damage_area || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m²`,
      child.damage_severity || child.damageSeverity || 'N/A',
      child.corrosion_category || child.corrosionCategory || 'N/A',
      child.paint_status || child.paintStatus || 'N/A',
      `${(child.paint_percentage || child.paintPercentage || 0).toFixed(1)}%`
    ]);
    
    autoTable(doc, {
      startY: yPosition,
      head: [[`Parent ${areaDataLevelLabel} Code`, `${childLevelLabel} Identifier`, 'Avg Area (m²)', 'Est. Damage (m²)', 'Current Damage (m²)', 'Damage', 'Corrosion', 'Paint Status', 'Paint %']],
      body: childElementsData,
      theme: 'striped',
      headStyles: { fillColor: [200, 200, 200] },
      styles: { fontSize: 7 }
    });
  }
  
  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(
      `Generated on ${new Date().toLocaleString()} - Page ${i} of ${pageCount}`,
      105,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
  }
  
  // Save
  doc.save(outputFilename);
};

/**
 * Export general data to PDF
 * @param {Array} data - Array of data objects
 * @param {Array} columns - Column definitions
 * @param {string} title - Report title
 * @param {string} filename - Output filename
 */
export const exportToPDF = (data, columns, title = 'Report', filename = null) => {
  const defaultFilename = `report_${Date.now()}.pdf`;
  const outputFilename = filename || defaultFilename;
  
  const doc = new jsPDF();
  
  // Title
  doc.setFontSize(20);
  doc.text(title, 105, 20, { align: 'center' });
  
  // Table
  const tableData = data.map(row => 
    columns.map(col => String(row[col.key] || ''))
  );
  
  autoTable(doc, {
    startY: 35,
    head: [columns.map(col => col.header)],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: [200, 200, 200] }
  });
  
  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(
      `Generated on ${new Date().toLocaleString()} - Page ${i} of ${pageCount}`,
      105,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
  }
  
  // Save
  doc.save(outputFilename);
};

